// Games view - displays user's active games

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import '../shared/confirm-delete.js';

/**
 * Games view component
 * Displays the user's active games with options to resume or delete
 */
class GamesView extends BaseComponent {
  static properties = {
    _games: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _showErrorModal: { type: Boolean, state: true },
    _errorMessage: { type: String, state: true },
    _gameToDelete: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Game>} */
    this._games = [];
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
    this._showDeleteModal = false;
    this._showErrorModal = false;
    this._errorMessage = '';
    /** @type {string | null} */
    this._gameToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadGames();
  }

  async _loadGames() {
    this._loading = true;
    this._error = null;

    try {
      this._games = /** @type {Array<import('../../types/jsdoc-types.js').Game>} */ (await api.listGames());
    } catch (err) {
      console.error('Failed to load games:', err);
      this._error = 'Failed to load games. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /** @param {string} dateString */
  _formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  /** @param {string} gameId */
  _handleResume(gameId) {
    router.navigate(`/game/${gameId}`);
  }

  /** @param {string} gameId */
  _handleDelete(gameId) {
    this._gameToDelete = gameId;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    this._showDeleteModal = false;

    if (!this._gameToDelete) return;

    try {
      await api.deleteGame(this._gameToDelete);
      this._gameToDelete = null;
      await this._loadGames();
    } catch (err) {
      console.error('Failed to delete game:', err);
      this._errorMessage = 'Failed to delete game. Please try again.';
      this._showErrorModal = true;
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._gameToDelete = null;
  }

  render() {
    return html`
                <div>
                        ${
                          this._loading
                            ? html`
                                <div class="text-center text-muted py-5">
                                  <div class="spinner-border" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                  </div>
                                  <p class="mt-3">Loading games...</p>
                                </div>
                              `
                            : this._error
                              ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
                              : this._games.length === 0
                                ? html`
                                    <div class="text-center text-muted py-5">
                                      <p>No active games. Start an adventure from the Store!</p>
                                    </div>
                                  `
                                : html`
                                    <div class="d-flex flex-wrap gap-3">
                                      ${this._games.map(
                                        (game) => html`
                                          <div style="flex: 1 1 300px; min-width: 300px; max-width: 350px;">
                                            <div class="card h-100">
                                              <div class="card-body d-flex flex-column">
                                                <h5 class="card-title">${game.adventureName || 'Unnamed Adventure'}</h5>
                                                <div class="text-muted small mb-3">
                                                  <div>Started: ${this._formatDate(game.createdAt || '')}</div>
                                                  <div>Last played: ${this._formatDate(game.updatedAt || '')}</div>
                                                </div>
                                                <div class="mt-auto d-flex gap-2">
                                                  <button
                                                    class="btn btn-primary flex-grow-1"
                                                    @click="${() => this._handleResume(game.id)}"
                                                  >
                                                    Resume
                                                  </button>
                                                  <button
                                                    class="btn btn-outline-danger"
                                                    @click="${() => this._handleDelete(game.id)}"
                                                  >
                                                    Delete
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        `,
                                      )}
                                    </div>
                                  `
                        }
                    </div>
                </div>

                ${
                  this._showDeleteModal
                    ? html`
                        <confirm-delete
                          ?show="${true}"
                          heading="Delete Game"
                          message="Are you sure you want to delete this game? This cannot be undone."
                          @confirm="${this._confirmDelete}"
                          @cancel="${this._cancelDelete}"
                        ></confirm-delete>
                      `
                    : ''
                }

                ${
                  this._showErrorModal
                    ? html`
                        <div class="modal d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
                          <div class="modal-dialog modal-dialog-centered">
                            <div class="modal-content">
                              <div class="modal-header">
                                <h5 class="modal-title">Error</h5>
                                <button
                                  type="button"
                                  class="btn-close"
                                  @click="${() => (this._showErrorModal = false)}"
                                ></button>
                              </div>
                              <div class="modal-body">
                                <p>${this._errorMessage}</p>
                              </div>
                              <div class="modal-footer">
                                <button
                                  type="button"
                                  class="btn btn-primary"
                                  @click="${() => (this._showErrorModal = false)}"
                                >
                                  OK
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      `
                    : ''
                }
        `;
  }
}

customElements.define('games-view', GamesView);
